import { ArrowRight, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';

import BookCover from '@/components/v2/primitives/BookCover';
import Reveal from '@/components/v2/primitives/Reveal';
import { LINK_COMUNIDADE_WHATSAPP } from '@/constants/links';
import { HUE_MARCA } from '@/design/hues';

/**
 * A API ora devolve os campos em português, ora em inglês, dependendo
 * do endpoint. A Home antiga já lia os dois — mantido o mesmo padrão
 * defensivo em vez de apostar num nome só.
 */
const campo = (evento, ...nomes) =>
  nomes.map((n) => evento?.[n]).find(Boolean) ?? null;

/**
 * Horários do encontro, lidos do array cru `dias`.
 *
 * Hoje `/eventos/proximo` devolve `dias: []`, então nada disto aparece.
 * Fica pronto para quando o backend passar a incluir a relação.
 *
 * NÃO usar `evento.diasFormatados`: o parseDateSafe de eventoFormatter
 * casa qualquer string que comece com YYYY-MM-DD e reconstrói a data
 * ao meio-dia, descartando a hora — o campo `horario` de lá sai sempre
 * "12:00".
 */
const horariosDoEvento = (evento) =>
  (evento?.dias ?? [])
    .map((dia) => {
      const d = new Date(dia.dataInicio);
      if (Number.isNaN(d.getTime())) return null;

      const vagas = dia.vagas ?? 0;
      const ocupadas = dia.vagasOcupadas ?? 0;

      return {
        id: dia.id,
        horario: new Intl.DateTimeFormat('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        }).format(d),
        restantes: Math.max(0, vagas - ocupadas),
        temVagas: vagas > 0,
        esgotado: vagas > 0 && ocupadas >= vagas,
      };
    })
    .filter(Boolean);

/**
 * Data da edição.
 *
 * Dois tipos de data convivem aqui, e cada um pede um fuso diferente:
 *
 * - `dias[].dataInicio` é um instante real (o encontro às 18h30), e a
 *   hora certa é a local.
 * - `referenceMonth` e `startDate` são datas puras, gravadas como
 *   meia-noite UTC. Formatá-las no fuso local desloca: em Manaus
 *   (UTC-4), 2025-10-01T00:00Z vira 30/09 às 20h, e outubro é
 *   anunciado como setembro. Por isso estas saem em UTC.
 */
const dataDaEdicao = (evento) => {
  const comHora = evento?.dias?.[0]?.dataInicio;
  const bruto =
    comHora ?? campo(evento, 'referenceMonth', 'startDate', 'dataInicio');
  if (!bruto) return null;

  const d = new Date(bruto);
  if (Number.isNaN(d.getTime())) return null;

  const emUtc = !comHora;
  return {
    mes: new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      timeZone: emUtc ? 'UTC' : undefined,
    }).format(d),
    ano: emUtc ? d.getUTCFullYear() : d.getFullYear(),
  };
};

/** Halos difusos que dão atmosfera à faixa tingida. */
const Halos = () => (
  <>
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -right-[8%] -top-[26%] h-[520px] w-[520px] rounded-full opacity-30 blur-[110px]"
      style={{ background: 'var(--hue)' }}
    />
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-[28%] -left-[6%] h-[360px] w-[360px] rounded-full opacity-45 blur-[100px]"
      style={{ background: 'var(--hue-soft)' }}
    />
  </>
);

/**
 * Sem encontro marcado a seção não pode simplesmente sumir: a Home
 * antiga avisava, e sumir faz parecer que o site quebrou. Aqui ela vira
 * um convite para a comunidade, que é onde as inscrições são anunciadas.
 */
const SemEncontro = () => (
  <section id="proxima-edicao" data-hue={HUE_MARCA} className="banda-creme">
    <div className="mx-auto w-full max-w-[1100px] px-5 py-14 md:px-6 md:py-20">
      {/* Em escala de título, e sem cartão: a banda já é a superfície,
          e quando não há encontro marcado é este aviso que carrega a
          seção inteira — o hero não tem mais texto para dividir o peso
          com ele. */}
      <Reveal>
        <p className="type-label mb-3 flex items-center gap-2.5 text-[var(--banda-acento)]">
          <span
            className="h-[3px] w-[24px] shrink-0 bg-[var(--banda-acento)]"
            aria-hidden="true"
          />
          Próxima edição
        </p>

        <h2 className="type-club mb-4 max-w-[17ch] text-[clamp(34px,6.4vw,76px)] text-[var(--banda-titulo)]">
          Ainda não há encontro marcado
        </h2>

        <p className="mb-9 max-w-[48ch] text-[clamp(15.5px,1.8vw,18px)] opacity-85">
          A data da próxima edição sai primeiro na comunidade. Entre no grupo
          do WhatsApp, que é gratuito, para saber antes de todo mundo.
        </p>

        <a
          href={LINK_COMUNIDADE_WHATSAPP}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2.5 rounded-full bg-[var(--banda-botao)] px-10 py-[19px] text-center text-[14px] font-bold uppercase tracking-[0.14em] text-[var(--banda-botao-tinta)] no-underline transition-transform duration-300 hover:-translate-y-0.5"
        >
          <CalendarDays size={18} strokeWidth={2.4} aria-hidden="true" />
          Entrar na comunidade
        </a>
      </Reveal>
    </div>
  </section>
);

export const ProximaEdicao = ({ evento, carregando }) => {
  if (carregando) {
    return (
      <section
        data-hue={HUE_MARCA}
        className="banda-creme py-14 md:py-20"
        aria-busy="true"
      >
        <div className="mx-auto w-full max-w-[1100px] px-5 md:px-6">
          <div className="h-64 animate-pulse bg-[color-mix(in_srgb,var(--banda-titulo)_12%,transparent)]" />
        </div>
      </section>
    );
  }

  if (!evento) return <SemEncontro />;

  const data = dataDaEdicao(evento);
  const horarios = horariosDoEvento(evento);
  const local = campo(evento, 'local', 'location');
  const slug = campo(evento, 'slug');
  // O livro sai do próprio evento publicado, e só dele.
  const tituloLivro = campo(evento, 'bookTitle', 'livroTitulo');
  const autorLivro = campo(evento, 'bookAuthor', 'livroAutor');
  const capa = campo(evento, 'bookCoverBase64', 'livroCapa');

  return (
    <section
      id="proxima-edicao"
      // A seção usa o rosa da marca, não o hue do mês: ela é a chamada
      // principal da home, e a cor da marca é que deve puxar o olho.
      // O hue por edição segue nas capas do acervo.
      data-hue={HUE_MARCA}
      className="banda-creme relative overflow-hidden py-14 md:py-20"
    >
      <Halos />

      {/* Três peças, colocadas na grade em vez de empilhadas: assim a
          capa pode cair entre o mês e o título no telefone, e saltar
          para a coluna da direita no desktop, sem duplicar nada no DOM.
          Em tela cheia ela ocupava a altura toda antes de qualquer
          palavra — daí ter encolhido e mudado de lugar. */}
      <div className="relative mx-auto grid w-full max-w-[1100px] grid-cols-1 gap-y-7 px-5 md:grid-cols-[minmax(0,1fr)_380px] md:items-center md:gap-x-16 md:gap-y-0 md:px-6">
        <Reveal className="row-start-1 md:col-start-1 md:row-start-1">
          <p className="mb-5 inline-flex items-center gap-2.5 rounded-full bg-white px-4 py-2 text-[10.5px] font-bold uppercase tracking-[0.18em] text-[var(--banda-acento)] shadow-[0_4px_14px_-6px_rgba(0,0,0,.25)]">
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--banda-titulo)]"
              aria-hidden="true"
            />
            Clube do livro
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--banda-titulo)]"
              aria-hidden="true"
            />
            Próxima edição
          </p>

          {data && (
            <>
              <p className="mb-2 flex items-center gap-4 text-[10.5px] font-bold uppercase tracking-[0.26em] text-[var(--banda-acento)]">
                Edição do mês
                <span
                  className="h-px w-14 shrink-0 bg-[var(--banda-fio)]"
                  aria-hidden="true"
                />
                <span className="type-data tracking-[0.2em]">{data.ano}</span>
              </p>

              {/* O mês é o maior elemento da home. Em caixa alta aqui — é
                  a exceção ao resto dos títulos, porque funciona como
                  carimbo de data, não como frase. */}
              <p className="type-club m-0 text-[clamp(52px,10.5vw,124px)] font-black uppercase leading-[.86] tracking-[-0.015em] text-[var(--banda-titulo)]">
                {data.mes}
              </p>
            </>
          )}
        </Reveal>

        {/* No telefone este bloco fica ao LADO da capa, não embaixo: é
            a legenda dela. O fio só existe no desktop, onde ele separa
            o mês do livro; no telefone cortaria meia coluna. */}
        <Reveal className="row-start-3 md:col-start-1 md:row-start-2">
          {tituloLivro && (
            <div className="md:border-t md:border-[var(--banda-fio)] md:pt-6">
              <p className="m-0 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="type-club text-[clamp(23px,5vw,52px)] font-bold uppercase tracking-[0.015em]">
                  {tituloLivro}
                </span>
                {autorLivro && (
                  <span className="type-literary text-[clamp(14px,1.9vw,21px)] opacity-80">
                    de {autorLivro}
                  </span>
                )}
              </p>
            </div>
          )}
        </Reveal>

        <Reveal className="row-start-4 md:col-start-1 md:row-start-3">

          {horarios.length > 0 && (
            <div className="mb-6">
              <p className="type-label mb-3 text-[var(--banda-acento)]">
                {horarios.length > 1 ? 'Escolha seu horário' : 'Horário'}
              </p>
              <ul className="flex list-none flex-wrap gap-2.5 p-0">
                {horarios.map((h) => (
                  <li key={h.id}>
                    <span
                      className={`flex flex-col gap-0.5 border px-4 py-2.5 ${
                        h.esgotado
                          ? 'border-outline-variant bg-transparent opacity-55'
                          : 'border-[var(--hue)] bg-[var(--hue-wash)]'
                      }`}
                    >
                      <b className="type-data text-base">{h.horario}</b>
                      <span className="type-label text-[9px] text-secondary">
                        {h.esgotado
                          ? 'esgotado'
                          : h.temVagas
                            ? `${h.restantes} vagas`
                            : 'disponível'}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Valores ficam na página do evento, não aqui. Um botão só: o
              "Sobre a obra" apontava para a Biblioteca, que deixou de
              existir, e link sem destino é pior que link nenhum. */}
          <div className="mt-8 flex w-full flex-col items-stretch gap-3.5 sm:w-auto sm:flex-row sm:items-center">
            <Link
              to={slug ? `/evento/${slug}` : '/'}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--banda-botao)] px-9 py-[18px] text-center text-[13px] font-bold uppercase tracking-[0.14em] text-[var(--banda-botao-tinta)] transition-transform duration-300 hover:-translate-y-0.5"
            >
              Garantir minha vaga
              <span
                aria-hidden="true"
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color-mix(in_srgb,var(--banda-botao-tinta)_22%,transparent)] transition-transform duration-300 group-hover:translate-x-1"
              >
                <ArrowRight size={15} strokeWidth={2.6} />
              </span>
            </Link>
          </div>
        </Reveal>

        {tituloLivro && (
          <Reveal className="group relative row-start-2 grid w-full place-items-center justify-self-center md:col-start-2 md:row-span-3 md:row-start-1 md:w-[380px] md:max-w-[380px]">
            {/* Os anéis ficam atrás da moldura, não em volta da capa: é
                a moldura inteira que é o objeto na mesa. No telefone
                saem — em volta de uma capa pequena viram rabisco. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 hidden place-items-center md:grid"
            >
              <span className="absolute aspect-square w-[108%] rounded-full border border-[var(--banda-fio)]" />
              <span className="absolute aspect-square w-[132%] rounded-full border border-[var(--banda-fio)] opacity-60" />
            </span>

            <div className="relative w-full min-w-0 max-w-full rounded-[18px] bg-white p-4 pb-3 shadow-[0_28px_54px_-22px_rgba(0,0,0,.36)] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] md:rounded-[20px] md:shadow-[0_34px_64px_-26px_rgba(0,0,0,.38)] md:group-hover:-translate-y-1.5">
              <div className="overflow-hidden rounded-[12px]">
                <BookCover
                  titulo={tituloLivro}
                  autor={autorLivro}
                  etiqueta="Lily Book Club"
                  capaBase64={capa}
                />
              </div>

              <div className="mt-3 flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1">
                <span className="type-label text-[9.5px] text-[var(--banda-acento)]">
                  Leitura oficial
                </span>
                <span className="type-label text-[9.5px] text-[#3a1620] opacity-70">
                  {local || 'Presencial'}
                </span>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default ProximaEdicao;
